function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useRef, useState } from 'react';
import cx from 'classnames';
import WithSx from '../../hoc/WithSX';
import useAriaAttributes from '../../../hooks/useAriaAttributes';
import getFocusableChildren from '../../../helpers/getFocusableChildren';
import Flex from '../Flex';
import Block from '../Block';
import Text from '../Text';
import { propTypes, defaultProps } from './props';

/**
 * @component InputGroup
 */
import { jsx as ___EmotionJSX } from "@emotion/react";
const InputGroup = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    classNames,
    sx,
    children,
    leadingBlock,
    trailingBlock,
    label,
    labelId,
    labelFor,
    isLabelForSROnly,
    error,
    helpText,
    variant,
    onClick
  } = props;
  const controlRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  /**
   * @function focusControlChildren
   * @description
   * @param {*} event
   */
  const focusControlChildren = event => {
    const firstFocusableElement = getFocusableChildren(controlRef?.current)?.[0];
    if (typeof firstFocusableElement?.focus === 'function') firstFocusableElement.focus();
    if (typeof onClick === 'function') onClick(event);
  };
  const containerCommonProps = {
    ref: ref,
    className: classNames?.container,
    sx: {
      ...sx?._container,
      ...(isHovered ? sx?.__hovered?._container : {}),
      ...(isFocused ? sx?.__focused?._container : {})
    }
  };
  const labelCommonProps = {
    as: 'label',
    className: cx('will-change-auto cursor-[inherit]', {
      'sr-only': isLabelForSROnly
    }, {
      'truncate max-w-[50%]': variant === 'INLINE'
    }, classNames?.label),
    sx: {
      ...sx?._label,
      ...(isHovered ? sx?.__hovered?._label : {}),
      ...(isFocused ? sx?.__focused?._label : {})
    },
    onClick: focusControlChildren
  };
  const controlCommonProps = {
    ref: controlRef,
    sx: {
      ...sx?._control,
      ...(isHovered ? sx?.__hovered?._control : {}),
      ...(isFocused ? sx?.__focused?._control : {})
    },
    onClick: focusControlChildren,
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false)
  };
  const errorCommonProps = {
    as: 'span',
    className: cx(classNames?.error),
    sx: sx?._error
  };
  const helpTextCommonProps = {
    as: 'span',
    className: cx(classNames?.helpText),
    sx: sx?._helpText
  };
  return {
    DEFAULT: ___EmotionJSX(Block, containerCommonProps, label && ___EmotionJSX(Block, null, ___EmotionJSX(Text, labelCommonProps, label)), ___EmotionJSX(Flex, _extends({
      className: cx('will-change-auto items-center', classNames?.control)
    }, controlCommonProps), leadingBlock, children, trailingBlock), error && ___EmotionJSX(Block, null, ___EmotionJSX(Text, errorCommonProps, error)), !error && helpText && ___EmotionJSX(Block, null, ___EmotionJSX(Text, helpTextCommonProps, helpText))),
    INSET: ___EmotionJSX(Block, containerCommonProps, ___EmotionJSX(Flex, _extends({
      className: cx('will-change-auto', classNames?.control)
    }, controlCommonProps), leadingBlock, ___EmotionJSX(Flex, {
      className: "flex-col"
    }, label && ___EmotionJSX(Text, labelCommonProps, label), children), trailingBlock), error && ___EmotionJSX(Block, null, ___EmotionJSX(Text, errorCommonProps, error)), !error && helpText && ___EmotionJSX(Block, null, ___EmotionJSX(Text, helpTextCommonProps, helpText))),
    INLINE: ___EmotionJSX(Block, containerCommonProps, ___EmotionJSX(Flex, _extends({
      className: cx('will-change-auto space-x-1 rtl:space-x-reverse items-center', classNames?.control)
    }, controlCommonProps), leadingBlock, label && ___EmotionJSX(Text, labelCommonProps, label), children, trailingBlock), error && ___EmotionJSX(Block, null, ___EmotionJSX(Text, errorCommonProps, error)), !error && helpText && ___EmotionJSX(Block, null, ___EmotionJSX(Text, helpTextCommonProps, helpText)))
  }[variant];
});
InputGroup.displayName = 'InputGroup';
InputGroup.propTypes = propTypes;
InputGroup.defaultProps = defaultProps;
export default WithSx(InputGroup, {
  _label: {},
  _control: {},
  _error: {}
});